import { useEffect, useRef } from 'react'

import { EMPTY_FUNCTION } from '../../../constants/common'
import { useConstant } from '../../../hooks'
import { preventDefault } from '../../../utils/events'

import {
  PRESS_ACTION_KEY_CODES,
  PRESS_ACTION_MOUSE_BUTTONS,
  PRESS_ACTION_TIMEOUT,
} from './PressAction.constants'

/**
 * @typedef {Object} PressActionComponentProps
 *
 * @property {boolean} disabled
 * @property {React.KeyboardEventHandler} onKeyDown
 * @property {React.KeyboardEventHandler} onKeyUp
 * @property {React.MouseEventHandler} onMouseDown
 * @property {React.MouseEventHandler} onMouseLeave
 * @property {React.MouseEventHandler} onMouseUp
 */

/**
 * @typedef {Partial<PressActionComponentProps>} PressActionProps
 * @template {*} TValue
 *
 * @property {React.ComponentType<PressActionComponentProps>} Component
 * @property {TValue} value
 * @property {(value: TValue) => void} [onAction]
 */

/**
 * The {@link PressAction} component is used to render a button-like component
 * that repeatedly calls the passed `onAction` handler while it is being pressed
 * on by the mouse or by the space bar.
 *
 * @template {*} TValue
 *
 * @param {PressActionProps<TValue>} props
 *
 * @returns {React.ReactElement<PressActionComponentProps>}
 */
function PressAction({
  Component,
  InnerComponent,
  disabled,
  keyCodes = PRESS_ACTION_KEY_CODES,
  mouseButtons = PRESS_ACTION_MOUSE_BUTTONS,
  timeout = PRESS_ACTION_TIMEOUT,
  value,
  onAction,
  onKeyDown,
  onKeyUp,
  onMouseDown,
  onMouseLeave,
  onMouseUp,
  ...componentProps
}) {
  const actionRef = useRef(EMPTY_FUNCTION)
  const actionIntervalRef = useRef(null)

  const clearActionInterval = useConstant(() => () => {
    clearInterval(actionIntervalRef.current)
    actionIntervalRef.current = null
  })

  /** @param {React.MouseEvent} event */
  function handleMouseDown(event) {
    if (mouseButtons.has(event.button) && !actionIntervalRef.current) {
      preventDefault(event)

      actionRef.current()
      actionIntervalRef.current = setInterval(
        () => actionRef.current(),
        timeout
      )
    }

    onMouseDown?.(event)
  }

  /** @param {React.MouseEvent} event */
  function handleMouseLeave(event) {
    clearActionInterval()
    onMouseLeave?.(event)
  }

  /** @param {React.MouseEvent} event */
  function handleMouseUp(event) {
    clearActionInterval()
    onMouseUp?.(event)
  }

  /** @param {React.KeyboardEvent} event */
  function handleKeyDown(event) {
    if (keyCodes.has(event.code) && !actionIntervalRef.current) {
      actionRef.current()
      actionIntervalRef.current = setInterval(
        () => actionRef.current(),
        PRESS_ACTION_TIMEOUT
      )
    }

    onKeyDown?.(event)
  }

  /** @param {React.KeyboardEvent} event */
  function handleKeyUp(event) {
    clearActionInterval()
    onKeyUp?.(event)
  }

  useEffect(() => {
    actionRef.current = () => {
      onAction?.(value)
    }
  }, [onAction, value])

  useEffect(() => {
    if (disabled) {
      clearActionInterval()
    }
  }, [clearActionInterval, disabled])

  useEffect(
    () => () => {
      clearActionInterval()
    },
    [clearActionInterval]
  )

  return (
    <Component
      {...componentProps}
      Component={InnerComponent}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
    />
  )
}

export default PressAction
